// normCalculator.js - Промпт 3 (расчёт норм по формулам)
// v0.12.0 — Метод «белок главный» (по философии пользователя)
//
// Новая логика (согласована 2026-08-02):
// 1. Калории = TDEE × (1 − дефицит по ИМТ). Дефицит 0–25% (спокойное похудение, без стресса).
//    Дефицит нарастает с ИМТ. Худым (ИМТ < 23) — 0%, поддержание.
// 2. Белок = max(вес × коэффициент по активности (2.0–2.4 г/кг),
//               0.33 × калории ÷ 4)  —  «нижний уровень белка 33%»
//    Предохранители:
//      - не больше 2.4 г/кг (безопасность почек / адекватность для спортсменов)
//      - не больше 40% калорий (чтобы не превращаться в «кето-мясо» и не убивать углеводы)
// 3. Жиры = 30% калорий (для гормонов и усвоения жирорастворимых витаминов), мин 30 г
// 4. Углеводы = остаток калорий, мин 100 г
// Итог БЖУ в типовых случаях ≈ 33–40 / 30 / 30–37

export const calculateNorms = (user) => {
  const { height, weight, age, gender, activity } = user;

  // ===== Шаг 1. Калории = TDEE × (1 − дефицит по ИМТ) =====
  // BMR по формуле Миффлина-Сан Жеора
  let bmr;
  if (gender === 'мужчина') {
    bmr = (10 * weight) + (6.25 * height) - (5 * age) + 5;
  } else {
    bmr = (10 * weight) + (6.25 * height) - (5 * age) - 161;
  }

  // Коэффициент активности (5 уровней)
  const activityMap = {
    'сидячий': 1.2,
    'малоактивный': 1.375,
    'активный': 1.55,
    'очень активный': 1.725,
    'экстра-активный': 1.9
  };
  const multiplier = activityMap[activity] || 1.2;

  // TDEE
  const tdee = bmr * multiplier;

  // ИМТ от текущего веса
  const heightM = height / 100;
  const imt = weight / (heightM * heightM);

  // Дефицит по ИМТ (спокойное похудение, не более 25%)
  let deficit = 0;
  if (imt >= 35) deficit = 0.25;
  else if (imt >= 30) deficit = 0.20;
  else if (imt >= 25) deficit = 0.15;
  else if (imt >= 23) deficit = 0.10;
  // ИМТ < 23 — дефицит 0 (норма/недостаток веса — поддержание)

  // Целевой вес больше не влияет на дефицит (убрано, чтобы не усложнять и не стрессовать)

  let dailyCalories = tdee * (1 - deficit);

  // Плавающая нижняя граница калорий (безопасность, без жёстких стрессов)
  const safeMin = gender === 'женщина' ? 1200 : 1500;
  const formulaMin = Math.round(tdee * 0.75); // не резать больше 25%
  const minCalories = Math.max(safeMin, formulaMin);
  if (dailyCalories < minCalories) dailyCalories = minCalories;

  // ===== Шаг 2. Белок — «главный макронутриент» =====

  // Вес для расчёта белка — всегда от текущего веса
  const proteinWeight = weight;

  // Коэффициент белка по активности (г/кг) — лёгкая зависимость
  const activityProteinMap = {
    'сидячий': 2.0,
    'малоактивный': 2.1,
    'активный': 2.2,
    'очень активный': 2.3,
    'экстра-активный': 2.4
  };
  const activityProteinCoef = activityProteinMap[activity] || 2.0;

  let dailyProteinGkg = Math.round(proteinWeight * activityProteinCoef);

  // Нижний уровень белка: минимум 33% от калорий
  const dailyProteinPct = Math.round(dailyCalories * 0.33 / 4);

  // Белок = max(г/кг по активности, 33% от калорий)
  let dailyProtein = Math.max(dailyProteinGkg, dailyProteinPct);

  // Предохранитель 1: не больше 2.4 г/кг (безопасность почек / адекватность)
  const capGkg = Math.round(proteinWeight * 2.4);
  // Предохранитель 2: не больше 40% от калорий (чтобы не кето и не убивать углеводы)
  const capPct = Math.round(dailyCalories * 0.40 / 4);

  dailyProtein = Math.min(dailyProtein, capGkg, capPct);

  // ===== Шаг 3. Жиры — 30% калорий (для гормонов) =====
  let dailyFat = Math.round((dailyCalories * 0.30) / 9);
  if (dailyFat < 30) dailyFat = 30; // минимум для гормонального фона

  // ===== Шаг 4. Углеводы — остаток калорий =====
  let dailyCarbs = Math.round((dailyCalories - (dailyProtein * 4) - (dailyFat * 9)) / 4);
  if (dailyCarbs < 100) dailyCarbs = 100; // минимум для энергии

  return {
    calories: Math.round(dailyCalories),
    protein: dailyProtein,
    fat: dailyFat,
    carbs: dailyCarbs,
    // диагностические поля для отладки
    _debug: {
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      multiplier,
      imt: Math.round(imt * 10) / 10,
      deficit: deficit * 100 + '%',
      minCalories,
      proteinCoef: activityProteinCoef,
      proteinWeight,
      proteinGkg: dailyProteinGkg,
      proteinPct: dailyProteinPct,
      capGkg,
      capPct
    }
  };
};
